import { TreeRoot } from "@/types";

export type DocumentTypeItem = {
  label: string;
  value: number | string;
  children?: DocumentTypeItem[];
};

export type DocumentRecord = {
  id: number;
  name: string;
  path: string;
  pp?: number | null;
  type?: number | null;
  program?: number | null;
  project?: number | null;
  origin?: "PROGRAM" | "PORTEUR" | "UNKNOWN" | string | null;
  subcontractorId?: number | null;
  subcontractorName?: string | null;
  intervenants?: Array<{
    id: number;
    firstname?: string | null;
    lastname?: string | null;
    fullname?: string | null;
    email?: string | null;
    type?: "intervenant" | "subcontractor" | string | null;
  }>;
};

export type DocumentTreeNode = {
  id: string;
  label: string;
  url?: string;
  children?: DocumentTreeNode[];
};

const LABEL_BY_TYPE: Record<number, string> = {
  1: "Statuts de l’association",
  2: "Présentation du projet",
  3: "Budget previsional",
  4: "IBAN",
  5: "CV ou biographie",
  7: "Cartographie du terrain (si applicable)",
  8: "Plan d’action / calendrier prévisionnel",
};

const PROJECT_DOC_TYPES = [2, 3, 7, 8];
const ACTOR_DOC_TYPES = [1, 5, 4];
const CONTRACTUAL_DOC_TYPES = [25, 26, 27];
const REPORT_DOC_TYPES = [10, 11, 12, 13, 14, 15];

const cleanName = (value?: string | null) =>
  (value ?? "")
    .replace(/^PP\s*-\s*/i, "")
    .replace(/^Prog\s*-\s*/i, "")
    .replace(/^Proj\s*-\s*/i, "")
    .trim();

const getUrl = (
  firstname: string,
  params: Record<string, string | number | Array<string | number> | undefined>,
) => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined) return;

    if (Array.isArray(value)) {
      if (value.length === 0) return;
      searchParams.set(key, value.join(","));
      return;
    }

    searchParams.set(key, String(value));
  });

  const query = searchParams.toString();
  return query
    ? `/${firstname}/mes-documents?${query}`
    : `/${firstname}/mes-documents`;
};

const getPpUrl = (
  firstname: string,
  ppId: number,
  params: Record<string, string | number | Array<string | number> | undefined> = {},
) => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined) return;

    if (Array.isArray(value)) {
      if (value.length === 0) return;
      searchParams.set(key, value.join(","));
      return;
    }

    searchParams.set(key, String(value));
  });

  const query = searchParams.toString();
  return query
    ? `/${firstname}/mes-documents/${ppId}?${query}`
    : `/${firstname}/mes-documents/${ppId}`;
};

export const normalizeDocumentTypes = (typesDocs: DocumentTypeItem[] = []) =>
  typesDocs.map((item) => ({
    ...item,
    children: (item.children ?? []).map((child) => ({
      ...child,
      label: LABEL_BY_TYPE[Number(child.value)] ?? child.label,
    })),
  }));

export const buildDocumentTree = ({
  firstname,
  documentsData: _documentsData,
  carrierTree = [],
}: {
  firstname: string;
  documentsData?: Record<string, DocumentRecord[]> | null;
  carrierTree?: TreeRoot[];
}): DocumentTreeNode[] => {
  const porteursById = new Map<number, { name: string }>();
  const programsById = new Map<number, { name: string; ppId: number; projects: { id: number; name: string }[] }>();
  const intervenantsById = new Map<number, string>();

  carrierTree.forEach((carrier) => {
    porteursById.set(carrier.id, {
      name: cleanName(carrier.name),
    });

    (carrier.intervenants ?? []).forEach((intervenant) => {
      if (!Number.isFinite(Number(intervenant?.id)) || Number(intervenant.id) <= 0) {
        return;
      }

      intervenantsById.set(
        Number(intervenant.id),
        intervenant.fullname?.trim()
          || [intervenant.firstname, intervenant.lastname].filter(Boolean).join(" ").trim()
          || `Intervenant #${intervenant.id}`,
      );
    });

    carrier.programs?.forEach((program) => {
      programsById.set(program.id, {
        name: cleanName(program.name),
        ppId: carrier.id,
        projects: (program.projects ?? []).map((project) => ({
          id: project.id,
          name: cleanName(project.name),
        })),
      });
    });
  });

  const mesPorteursChildren = [...porteursById.entries()]
    .map(([ppId, porteur]) => {
      const children: DocumentTreeNode[] = ACTOR_DOC_TYPES
        .map((type) => ({
          id: `pp-${ppId}-type-${type}`,
          label: LABEL_BY_TYPE[type],
          url: getPpUrl(firstname, ppId, {
            categories: [type],
          }),
        }));

      children.push({
        id: `pp-${ppId}-contractuel`,
        label: "Contractuel",
        url: getPpUrl(firstname, ppId, {
          categories: CONTRACTUAL_DOC_TYPES,
        }),
      });

      children.push({
        id: `pp-${ppId}-rapport`,
        label: "Rapport",
        url: getPpUrl(firstname, ppId, {
          categories: REPORT_DOC_TYPES,
        }),
      });

      return {
        id: `pp-${ppId}`,
        label: porteur.name,
        url: getPpUrl(firstname, ppId),
        children,
      };
    }) as DocumentTreeNode[];

  const intervenantsChildren = [...intervenantsById.entries()]
    .sort((left, right) => String(left[1]).localeCompare(String(right[1]), "fr"))
    .map(([intervenantId, intervenantName]) => ({
      id: `intervenant-${intervenantId}`,
      label: intervenantName,
      url: getUrl(firstname, {
        origin: "PROGRAM",
        intervenants: [intervenantId],
      }),
      children: [
        ...ACTOR_DOC_TYPES.map((type) => ({
          id: `intervenant-${intervenantId}-type-${type}`,
          label: LABEL_BY_TYPE[type],
          url: getUrl(firstname, {
            origin: "PROGRAM",
            intervenants: [intervenantId],
            categories: [type],
          }),
        })),
        {
          id: `intervenant-${intervenantId}-contractuel`,
          label: "Contractuel",
          url: getUrl(firstname, {
            origin: "PROGRAM",
            intervenants: [intervenantId],
            categories: CONTRACTUAL_DOC_TYPES,
          }),
        },
        {
          id: `intervenant-${intervenantId}-rapport`,
          label: "Rapport",
          url: getUrl(firstname, {
            origin: "PROGRAM",
            intervenants: [intervenantId],
            categories: REPORT_DOC_TYPES,
          }),
        },
      ],
    }));

  const mesProjetsChildren = [...programsById.entries()].map(([programId, program]) => ({
    id: `program-${programId}`,
    label: program.name,
    url: getUrl(firstname, {
      pps: [program.ppId],
      programs: [programId],
    }),
    children: program.projects.map((project) => ({
      id: `project-${project.id}`,
      label: project.name,
      url: getUrl(firstname, {
        pps: [program.ppId],
        programs: [programId],
        projects: [project.id],
      }),
      children: [
        ...PROJECT_DOC_TYPES.map((type) => ({
          id: `project-${project.id}-type-${type}`,
          label: LABEL_BY_TYPE[type],
          url: getUrl(firstname, {
            pps: [program.ppId],
            programs: [programId],
            projects: [project.id],
            categories: [type],
          }),
        })),
        {
          id: `project-${project.id}-photos`,
          label: "Photos",
          url: getUrl(firstname, {
            pps: [program.ppId],
            programs: [programId],
            projects: [project.id],
            types: ["image"],
          }),
        },
      ],
    })),
  }));

  return [
    {
      id: "mes-porteurs-projets",
      label: "Mes porteurs de projets",
      // url: `/${firstname}/mes-documents`,
      children: [
        ...mesPorteursChildren,
        {
          id: "dossier-intervenants",
          label: "Dossier intervenant",
          children: intervenantsChildren,
        },
      ],
    },
    {
      id: "mes-projets",
      label: "Mes projets",
      // url: `/${firstname}/mes-documents`,
      children: mesProjetsChildren,
    },
  ];
};
